// frontend/packages/business-console/src/pages/Invoices/InvoiceDocumentView.jsx
//
// WYSIWYG document body for the Invoice detail page. Mirrors the
// shape of QuoteDocumentView with invoice-specific extras:
//   - "INVOICE" label (vs "QUOTE")
//   - Amount-due block in the header
//   - dueAt date prominent (invoices live or die by due dates)
//   - Subtle "PAID" / "VOID" overprint stamp when terminal

import React from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDate(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function customerDisplayName(customer) {
  if (!customer) return 'Customer';
  if (customer.companyName) return customer.companyName;
  const fn = customer.firstName || '';
  const ln = customer.lastName || '';
  const joined = `${fn} ${ln}`.trim();
  return joined || 'Customer';
}

function customerAddressLines(customer) {
  if (!customer || !customer.address) return [];
  const a = customer.address;
  const lines = [];
  if (a.line1) lines.push(a.line1);
  if (a.line2) lines.push(a.line2);
  const cityLine = [a.city, a.state].filter(Boolean).join(', ');
  const csz = [cityLine, a.postalCode].filter(Boolean).join('  ');
  if (csz) lines.push(csz);
  return lines;
}

export default function InvoiceDocumentView({ invoice, customer, business }) {
  if (!invoice) return null;

  const stamp =
    invoice.state === 'paid'
      ? { text: 'PAID', color: 'success.main' }
      : invoice.state === 'void'
        ? { text: 'VOID', color: 'text.secondary' }
        : null;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        maxWidth: 720,
        width: '100%',
        fontFamily:
          "'Liberation Sans', 'Noto Sans', Arial, system-ui, sans-serif",
      }}
    >
      {/* PAID / VOID overprint stamp */}
      {stamp ? (
        <Box
          sx={{
            position: 'absolute',
            top: '30%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-14deg)',
            pointerEvents: 'none',
            zIndex: 1,
            border: '4px solid',
            borderColor: stamp.color,
            color: stamp.color,
            opacity: 0.18,
            fontSize: 80,
            fontWeight: 900,
            letterSpacing: '0.12em',
            px: 3,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          {stamp.text}
        </Box>
      ) : null}

      <Box sx={{ p: { xs: 2, sm: 5 }, position: 'relative', zIndex: 0 }}>
        {/* ── Header: business identity (left) + Invoice label (right) ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.01em',
              }}
            >
              {business?.name || ''}
            </Typography>
            {business?.address ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
              >
                {business.address}
              </Typography>
            ) : null}
            {business?.email ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {business.email}
              </Typography>
            ) : null}
            {business?.phone ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {business.phone}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.18em',
                color: 'text.secondary',
                textTransform: 'uppercase',
              }}
            >
              Invoice
            </Typography>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'text.primary',
                mt: 0.5,
              }}
            >
              {invoice.number}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
            >
              Issued {formatDate(invoice.createdAt)}
            </Typography>
            {invoice.dueAt ? (
              <Typography
                variant="caption"
                sx={{
                  color:
                    invoice.state === 'overdue'
                      ? 'error.main'
                      : 'text.secondary',
                  fontWeight: invoice.state === 'overdue' ? 700 : 400,
                  display: 'block',
                }}
              >
                Due {formatDate(invoice.dueAt)}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ── Bill-to + amount-due summary side by side ──────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
            gap: 3,
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Bill to
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mt: 0.5,
                fontSize: 15,
              }}
            >
              {customerDisplayName(customer)}
            </Typography>
            {customer?.email ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {customer.email}
              </Typography>
            ) : null}
            {customerAddressLines(customer).map((line, idx) => (
              <Typography
                key={idx}
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {line}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              textAlign: { xs: 'left', sm: 'right' },
              minWidth: 160,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Amount due
            </Typography>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
                color:
                  invoice.state === 'paid'
                    ? 'success.main'
                    : invoice.state === 'overdue'
                      ? 'error.main'
                      : 'text.primary',
                fontVariantNumeric: 'tabular-nums',
                mt: 0.25,
              }}
            >
              {invoice.state === 'paid' ? '$0.00' : fmtUsd(invoice.amountDue)}
            </Typography>
            {invoice.amountPaid > 0 && invoice.state !== 'paid' ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
              >
                {fmtUsd(invoice.amountPaid)} paid · {fmtUsd(invoice.total)}{' '}
                total
              </Typography>
            ) : null}
          </Box>
        </Box>

        {invoice.title ? (
          <Typography
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mt: 3,
              fontSize: 14,
            }}
          >
            {invoice.title}
          </Typography>
        ) : null}

        <Divider sx={{ my: 3 }} />

        {/* ── Line items ────────────────────────────────────────── */}
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr 32px 66px 72px',
                sm: '1fr 60px 100px 110px',
              },
              gap: 1,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <HeaderCell>Description</HeaderCell>
            <HeaderCell align="right">Qty</HeaderCell>
            <HeaderCell align="right">Unit price</HeaderCell>
            <HeaderCell align="right">Line total</HeaderCell>
          </Box>
          {(invoice.lineItems || []).map((li) => (
            <Box
              key={li.id || li.position}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr 32px 66px 72px',
                  sm: '1fr 60px 100px 110px',
                },
                gap: 1,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                alignItems: 'baseline',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.primary',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {li.description || '—'}
                </Typography>
              </Box>
              <Cell align="right">{li.quantity}</Cell>
              <Cell align="right">{fmtUsd(li.unitPrice)}</Cell>
              <Cell align="right" bold>
                {fmtUsd(li.lineTotal)}
              </Cell>
            </Box>
          ))}
        </Box>

        {/* ── Totals (right-aligned column) ─────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
          }}
        >
          <Stack
            spacing={0.75}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 0, sm: 240 },
              '& > *': {
                display: 'flex',
                justifyContent: 'space-between',
              },
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Subtotal
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  color: 'text.primary',
                }}
              >
                {fmtUsd(invoice.subtotal)}
              </Typography>
            </Box>
            {invoice.discountTotal > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Discount
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'text.primary',
                  }}
                >
                  −{fmtUsd(invoice.discountTotal)}
                </Typography>
              </Box>
            ) : null}
            {invoice.taxTotal > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Tax
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'text.primary',
                  }}
                >
                  {fmtUsd(invoice.taxTotal)}
                </Typography>
              </Box>
            ) : null}
            <Divider sx={{ mt: 0.5, mb: 0.5 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Total
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtUsd(invoice.total)}
              </Typography>
            </Box>
            {invoice.amountPaid > 0 ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'success.main', fontWeight: 600 }}
                >
                  Paid
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'success.main',
                    fontWeight: 600,
                  }}
                >
                  −{fmtUsd(invoice.amountPaid)}
                </Typography>
              </Box>
            ) : null}
            {invoice.amountRefunded > 0 ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'warning.main', fontWeight: 600 }}
                >
                  Refunded
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    color: 'warning.main',
                    fontWeight: 600,
                  }}
                >
                  +{fmtUsd(invoice.amountRefunded)}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Box>

        {/* ── Notes + terms ─────────────────────────────────────── */}
        {invoice.notes || invoice.terms ? (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            {invoice.notes ? (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Notes
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: 'text.primary',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {invoice.notes}
                </Typography>
              </Box>
            ) : null}
            {invoice.terms ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Terms
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {invoice.terms}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
}

function HeaderCell({ children, align }) {
  return (
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        textAlign: align || 'left',
        fontSize: 10,
      }}
    >
      {children}
    </Typography>
  );
}

function Cell({ children, align, bold }) {
  return (
    <Typography
      sx={{
        fontSize: 13,
        color: 'text.primary',
        textAlign: align || 'left',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: bold ? 700 : 400,
      }}
    >
      {children}
    </Typography>
  );
}
